import { useState } from 'react'
import { AlertTriangle, Check, FilePlus2, LoaderCircle, RefreshCw } from 'lucide-react'
import { api } from '../lib/apiClient'
import type { AppRecord } from '../types'
import { Modal } from './Modal'

type CustomerQuoteResult = {
  customerQuote: { version: number; fileName: string; url: string; generatedAt: string; language: 'PT' }
  warnings: string[]
}

function attachmentCount(value: unknown) {
  return Array.isArray(value) ? value.length : 0
}

function readiness(record: AppRecord) {
  const missing: string[] = []
  if (record.customerQuoteSentAt) missing.push('an editable customer draft (this quotation was marked sent)')
  if (String(record.status || '').toLowerCase() === 'superseded') missing.push('a non-archived customer Quote')
  if (record.pricingSource === 'quote_items' && !record.customerPreparationReady) missing.push('current customer Quote preparation')
  if (!record.quoteCode || !record.quoteNumber) missing.push('Quote ID and number')
  if (!record.opportunityId) missing.push('linked opportunity')
  if (String(record.pricingStatus || '').toLowerCase() !== 'calculated') missing.push('Calculated pricing status')
  if (!attachmentCount(record.proposedQuote)) missing.push('proposed quote data')
  return missing
}

export function CreateCustomerQuoteButton({ record, reload }: { record: AppRecord; reload: () => Promise<void> }) {
  const [confirming, setConfirming] = useState(false)
  const [running, setRunning] = useState(false)
  const [notice, setNotice] = useState<{ kind: 'success' | 'warning' | 'error'; message: string } | null>(null)
  const missing = readiness(record)
  const hasExisting = attachmentCount(record.customerQuotePdf) > 0
  const alreadyRunning = String(record.customerQuoteStatus || '').toLowerCase() === 'generating'
  const disabled = Boolean(missing.length || running || alreadyRunning)

  const generate = async (force: boolean) => {
    setConfirming(false)
    setRunning(true)
    setNotice(null)
    try {
      const result = await api.action<CustomerQuoteResult>('quotes', record.id, 'customer-quote', force ? { force: true } : {})
      await reload()
      const version = String(result.customerQuote.version).padStart(3, '0')
      setNotice(result.warnings.length
        ? { kind: 'warning', message: `Customer quotation v${version} was generated. ${result.warnings.join(' ')}` }
        : { kind: 'success', message: `Customer quotation v${version} was generated and attached to Airtable.` })
    } catch (error) {
      setNotice({ kind: 'error', message: error instanceof Error ? error.message : 'The customer quotation could not be generated.' })
      await reload().catch(() => undefined)
    } finally {
      setRunning(false)
    }
  }

  const begin = () => {
    if (hasExisting) setConfirming(true)
    else void generate(false)
  }

  return (
    <>
      <div className="relative">
        <button
          className="btn-primary"
          disabled={disabled}
          title={missing.length ? `Required: ${missing.join(', ')}` : alreadyRunning ? 'Generation is already in progress.' : undefined}
          onClick={begin}
        >
          {running || alreadyRunning ? <LoaderCircle className="h-4 w-4 animate-spin" /> : hasExisting ? <RefreshCw className="h-4 w-4" /> : <FilePlus2 className="h-4 w-4" />}
          {running || alreadyRunning ? 'Generating…' : hasExisting ? 'Regenerate Customer Quote' : 'Create Customer Quote'}
        </button>
      </div>

      {notice && (
        <div className={`fixed bottom-5 right-5 z-50 flex max-w-md items-start gap-2 rounded-xl border px-4 py-3 text-sm font-semibold shadow-lg ${notice.kind === 'success' ? 'border-pine-200 bg-pine-50 text-pine-800' : notice.kind === 'warning' ? 'border-amber-200 bg-amber-50 text-amber-900' : 'border-rose-200 bg-rose-50 text-rose-800'}`}>
          {notice.kind === 'success' ? <Check className="mt-0.5 h-4 w-4 shrink-0" /> : <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />}
          <span>{notice.message}</span>
          <button className="ml-2 text-current opacity-60 hover:opacity-100" onClick={() => setNotice(null)} aria-label="Dismiss">×</button>
        </div>
      )}

      <Modal
        open={confirming}
        onClose={() => !running && setConfirming(false)}
        title="Regenerate customer quotation?"
        description="A new version will be generated and appended in Airtable. Existing PDF versions will be retained."
      >
        <div className="flex justify-end gap-3">
          <button className="btn-secondary" disabled={running} onClick={() => setConfirming(false)}>Cancel</button>
          <button className="btn-primary" disabled={running} onClick={() => void generate(true)}>
            {running ? <LoaderCircle className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            Generate new version
          </button>
        </div>
      </Modal>
    </>
  )
}
